import { faSidebar } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useRef } from 'react';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { IconButton } from 'atoms/buttons/IconButton';
import { SorareLogo } from 'atoms/icons/SorareLogo';
import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { LANDING } from 'constants/__generated__/routes';
import { useSportContext } from 'contexts/sport';
import { useIsLoggedIn } from 'hooks/auth/useIsLoggedIn';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useIsMobileApp } from 'hooks/useIsMobileApp';
import { STORAGE, useLocalStorage } from 'hooks/useLocalStorage';
import { generateSportPath } from 'lib/routing/generateSportPath';
import { laptopAndAbove } from 'style/mediaQuery';

import { GlobalStyles } from './GlobalStyles';
import { NavItems } from './NavItems';
import { SearchBar } from './SearchBar';
import { SportSelector } from './SportSelector';
import { UserCurrencies } from './UserCurrencies';
import { useBottomBarScroll } from './useBottomBarScroll';

const MIN_BOTTOM_PADDING_MOBILE_APP = 16;

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: sticky;
  z-index: 2;
  bottom: 0;

  &.hideBottomBar {
    transform: translateY(
      calc(var(--navbar-height-mobile) + var(--safe-area-inset-bottom))
    );
  }
  transition: 300ms transform ease-in-out;

  @media ${laptopAndAbove} {
    position: sticky;
    top: 0;
    height: 100vh;
    width: 240px;

    &.sidebarCollapsed {
      width: 68px;
    }
    transition: 300ms width ease-in-out;
  }
`;
const Nav = styled(Horizontal)`
  width: 100%;
  height: calc(var(--navbar-height-mobile) + var(--safe-area-inset-bottom));
  gap: 0;
  background-color: var(--c-black);
  border-top: 1px solid var(--c-nd-50);
  padding-bottom: var(--safe-area-inset-bottom);

  @media ${laptopAndAbove} {
    flex: 1;
    flex-direction: column;
    gap: var(--double-unit);
    border-top: none;
    border-right: 1px solid var(--c-nd-150);
    padding: var(--intermediate-unit);
  }
`;
const FullWidthSBHorizontal = styled(SBHorizontal)`
  width: 100%;

  .sidebarCollapsed & {
    flex-direction: column;
  }
`;
const SportSelectorWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  .sidebarCollapsed & {
    flex-direction: column;
    gap: var(--double-unit);
  }
`;
const LogoWrapper = styled(Horizontal)`
  max-width: 100px;
  height: 40px;
`;
const PortalReceiver = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  justify-content: flex-start;
  align-items: stretch;
`;
const CurrenciesWrapper = styled.div`
  width: 100%;
  margin-top: auto;
`;

export const MobileAppBottomPortalReceiver = styled(PortalReceiver)`
  padding-bottom: var(--double-unit);
  &.isAndroidApp {
    padding-bottom: 0;
  }
`;

export const AppNavigation = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(
    STORAGE.navigationSidebarCollapsed,
    false
  );
  const { sport } = useSportContext();
  const navigationRef = useRef<HTMLElement>(null);
  const isLaptopAndAbove = useIsLaptop();
  const loggedIn = useIsLoggedIn();
  const { isMobileApp, hideBottomNavBar } = useIsMobileApp();
  const hideBottomBar = useBottomBarScroll(navigationRef);

  if (!loggedIn) {
    return null;
  }

  if (isMobileApp && hideBottomNavBar) {
    return (
      <Root>
        <GlobalStyles noHeight />
        <PortalReceiver id="above-bottom-bar-portal" />
      </Root>
    );
  }

  return (
    <>
      <GlobalStyles
        noHeight={isLaptopAndAbove || hideBottomBar}
        minSafeAreaInsetBottom={isMobileApp ? MIN_BOTTOM_PADDING_MOBILE_APP : 0}
      />
      <Root
        className={classNames({
          hideBottomBar,
          sidebarCollapsed: sidebarCollapsed && isLaptopAndAbove,
        })}
      >
        {!isLaptopAndAbove && <PortalReceiver id="above-bottom-bar-portal" />}
        <Nav as="nav" ref={navigationRef}>
          {!!isLaptopAndAbove && (
            <>
              <FullWidthSBHorizontal>
                {!sidebarCollapsed && (
                  <LogoWrapper
                    as={Link}
                    to={generateSportPath(LANDING, { sport })}
                  >
                    <SorareLogo size="small" />
                  </LogoWrapper>
                )}
                <SportSelectorWrapper>
                  <SportSelector withoutChevronIcon={sidebarCollapsed} />
                  {!!isLaptopAndAbove && (
                    <IconButton
                      size="medium"
                      color="transparent"
                      icon={faSidebar}
                      onClick={() => setSidebarCollapsed(prev => !prev)}
                    />
                  )}
                </SportSelectorWrapper>
              </FullWidthSBHorizontal>
              {!sidebarCollapsed && <SearchBar />}
            </>
          )}
          <NavItems sport={sport} sidebarCollapsed={sidebarCollapsed} />
          {!!isLaptopAndAbove && (
            <CurrenciesWrapper>
              <UserCurrencies sidebarCollapsed={sidebarCollapsed} />
            </CurrenciesWrapper>
          )}
        </Nav>
      </Root>
    </>
  );
};
